import { cx } from 'emotion';
import { isEmpty, isNil, reverse } from 'ramda';
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
  useMemo,
  CSSProperties,
} from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useDispatch, useSelector } from 'react-redux';

import { Spinner } from '@ezugi/components';
import { useOrientationChange } from '@ezugi/hooks/src/useOrientationChange';
import {
  ErrorFallback,
  Scrollable as ScrollableJS,
} from '@ezugi/primitives';
import closeIcon from '@ezugi/static/icons/close.svg';
import { GenericPayloadType } from '@ezugi/types';
import Drawer from '@ezugi/ui/components/Drawer/Drawer';
import { DeviceInfo } from '@ezugi/utils';

import { useIntl } from '../../hooks/useIntl';
import { lobbyActions } from '../../store/actions';
import {
  availableTableGroupsIdsSelector,
  lobbyGameGroupsConfigSelector,
  lobbyGamesGroupIdsKeySelector,
  lobbyLoadingSelector,
  lobbyOpenMenuWidgetSelector,
  lobbyOpenSelector,
} from '../../store/selectors/lobby';
import { handleErrorBoundary } from '../../util/errorBoundary';
import {
  drawerContainer, header, mobileDrawerContainer, miniLobbyPanelClassName,
  drawerHeaderClassName, drawerBodyClassName,
  spinnerContainer,
  bodyStyle,
  container,
} from './GameLobby.css';

const Scrollable = ScrollableJS as any;

const MiniLobby = lazy(() => import('../../components/MiniLobby'));
const MiniLobbyNavBar = lazy(() => import('../../components/MiniLobby/NavBar'));

type GameLobbyInnerProps = {
  onDrawerClose: () => void;
} & GameLobbyProps;

const GameLobbyInner = ({
  onDrawerClose,
  renderInsideDrawer,
  selectInitialGroupByCurrentTable,
  align = 'left',
  style,
  className,
}: GameLobbyInnerProps) => {
  const dispatch = useDispatch();

  const open = useSelector(lobbyOpenSelector) ?? true;

  const openMenuWidget = useSelector(lobbyOpenMenuWidgetSelector);
  const loading = useSelector(lobbyLoadingSelector);
  const availableTableGroupsIdsKey = useSelector(
    availableTableGroupsIdsSelector
  );
  const gamesGroupsIdsKey = useSelector(lobbyGamesGroupIdsKeySelector) ?? '';
  const gameGroupsFromConfigFilters = useSelector(lobbyGameGroupsConfigSelector);

  const navigate = (key: GenericPayloadType) => {
    dispatch(lobbyActions.lobby.navigate(key));
  };

  const [ activeGameGroupId, setActiveGameGroupId ] = useState<number | null>(
    null
  );
  const orientation = useOrientationChange();

  const availableTableGroupsIds = useMemo(
    () => availableTableGroupsIdsKey.split(','),
    [ availableTableGroupsIdsKey ]
  );
  const gamesGroupsIds = useMemo(
    () => new Set(gamesGroupsIdsKey.split(',')),
    [ gamesGroupsIdsKey ]
  );

  useEffect(() => {
    // gameGroups not loaded
    if (gamesGroupsIdsKey === '') {
      return;
    }

    // find the first game group that includes the current table and has other open tables
    const firstTableGroupDisplayed = availableTableGroupsIds.find((gr) => gamesGroupsIds.has(gr));

    // if there are no other tables open we fallback to the first game group in the navbar
    const [ firstGameGroup ] = gamesGroupsIds;

    const fallbackGroupId = !isNil(firstTableGroupDisplayed)
      ? firstTableGroupDisplayed
      : firstGameGroup;
    let initialTableGroupId = gamesGroupsIds.has(`${activeGameGroupId}`)
      ? activeGameGroupId
      : fallbackGroupId;

    // if selectInitialGroupByCurrentTable is true (e.g Baccarat case)
    // we want to reset the tab to the origin game group, i.e Baccarat
    // origin game group is always the last element of availableTableGroupsIds
    if (selectInitialGroupByCurrentTable) {
      const lastTableGroupDisplayedIndex = reverse(
        availableTableGroupsIds
      ).findIndex((group) => gamesGroupsIds.has(group));
      // index = 0 in reversed is last index of availableTableGroupsIds
      if (
        availableTableGroupsIds.length
        && lastTableGroupDisplayedIndex === 0
      ) {
        initialTableGroupId = availableTableGroupsIds[availableTableGroupsIds.length - 1];
      }
    }

    const newActiveGroupId = initialTableGroupId === null
      ? null
      : parseInt(`${initialTableGroupId}`, 10);

    setActiveGameGroupId(newActiveGroupId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ open, openMenuWidget, gamesGroupsIdsKey, availableTableGroupsIdsKey ]);

  const onTableGroupChange = useCallback(
    (code: number) => {
      if (code !== activeGameGroupId) {
        setActiveGameGroupId(code);
        dispatch(lobbyActions.lobby.categoryClick(
          // eslint-disable-next-line camelcase
          { category_id: code, category_name: gameGroupsFromConfigFilters[code]?.name }
        ));
      }
    },
    [ activeGameGroupId, dispatch, gameGroupsFromConfigFilters ]
  );

  const body = (
    <div className={container}>
      <Suspense fallback={null}>
        {!loading && !isEmpty(gamesGroupsIdsKey) ? (
          <div className={header} data-e2e="minilobby-drawer-head">
            <MiniLobbyNavBar
              activeGameGroupId={activeGameGroupId}
              onTableGroupChange={onTableGroupChange}
            />
          </div>
        ) : null}
        <div className={bodyStyle} data-e2e="minilobby-drawer-body">
          {loading ? (
            <div className={spinnerContainer}>
              <Spinner />
            </div>
          ) : (
            <Scrollable>
              <MiniLobby
                columns={DeviceInfo.isHandheld ? (orientation === 'portrait' ? 1 : 2) : 5}
                activeGameGroupId={activeGameGroupId}
                // @ts-expect-error
                onLimitClick={navigate}
                hideContent={(!open && !openMenuWidget)}
              />
            </Scrollable>
          )}
        </div>
      </Suspense>
    </div>
  );

  return renderInsideDrawer ? (
    <div
      className={cx(
        drawerContainer,
        { [mobileDrawerContainer]: DeviceInfo.isHandheld },
        className
      )}
      style={style}
      data-e2e="minilobby"
    >
      <Drawer
        data-e2e="minilobby-drawer"
        data-open={open}
        open={open}
        align={align}
        headerClassName={drawerHeaderClassName}
        bodyClassName={drawerBodyClassName}
        panelClassName={miniLobbyPanelClassName}
        rightIcon={closeIcon}
        onRightIconClick={onDrawerClose}
      >
        {body}
      </Drawer>
    </div>
  ) : (
    body
  );
};

export type GameLobbyProps = {
  renderInsideDrawer?: boolean;
  align?: 'right' | 'left';
  className?: string;
  style?: CSSProperties;
  selectInitialGroupByCurrentTable?: boolean;
};

const GameLobby = ({
  renderInsideDrawer = true,
  selectInitialGroupByCurrentTable = false,
  className,
  style,
  align = 'left',
}: GameLobbyProps) => {
  const dispatch = useDispatch();

  const onDrawerClose = useCallback(() => {
    dispatch(lobbyActions.lobby.close());
  }, [ dispatch ]);

  return (
    <ErrorBoundary
      fallback={(
        <GameLobbyFallback
          align={align}
          className={className}
          style={style}
          renderInsideDrawer={renderInsideDrawer}
          onDrawerClose={onDrawerClose}
        />
      )}
      onError={handleErrorBoundary}
    >
      <GameLobbyInner
        align={align}
        className={className}
        style={style}
        renderInsideDrawer={renderInsideDrawer}
        onDrawerClose={onDrawerClose}
        selectInitialGroupByCurrentTable={selectInitialGroupByCurrentTable}
      />
    </ErrorBoundary>
  );
};

const GameLobbyFallback = ({
  align = 'left',
  className,
  style,
  onDrawerClose,
  renderInsideDrawer,
}: GameLobbyInnerProps) => {
  const open = useSelector(lobbyOpenSelector) ?? true;

  const locale = useIntl({
    keys: {
      errorMessage: 'errors.something_went_wrong',
    },
  });

  const body = (
    <div className={container}>
      <div className={bodyStyle} data-e2e="minilobby-drawer-body">
        <div className={spinnerContainer}>
          <ErrorFallback message={locale.errorMessage} />
        </div>
      </div>
    </div>
  );

  return renderInsideDrawer ? (
    <div
      className={cx(
        drawerContainer,
        { [mobileDrawerContainer]: DeviceInfo.isHandheld },
        className
      )}
      style={style}
      data-e2e="minilobby"
    >
      <Drawer
        data-e2e="minilobby-drawer"
        data-open={open}
        open={open}
        align={align}
        headerClassName={drawerHeaderClassName}
        bodyClassName={drawerBodyClassName}
        panelClassName={miniLobbyPanelClassName}
        rightIcon={closeIcon}
        onRightIconClick={onDrawerClose}
      >
        {body}
      </Drawer>
    </div>
  ) : (
    body
  );
};

export default React.memo(GameLobby);
